import React, { useState, useEffect, useRef } from 'react'
import {
    PharmacyGetAuthenticationPhoneNumber,
    PharmacyUpdateAuthenticationPhoneNumber
} from '../../../services/reducers/pharmacy/other/commonReducerForInitalData'

import { useCommonReducerSelector } from '../../../services/selectors/commonReducerSelector'
import { ProfilePicLoader } from '../../../components/common/loadingView'
import { ModalCloseIcon } from '../../../assets/svg/icons'
import { useDispatch } from 'react-redux'

const UpdateAuthenticationNumber = () => {
    const [showModal, setShowModal] = useState(false)
    const [primaryPhoneNumber, setPrimaryPhoneNumber] = useState('')
    const [additionalPhones, setAdditionalPhones] = useState([])
    const [additionalPhoneErrors, setAdditionalPhoneErrors] = useState([]) 
    const updateSubmittedRef = useRef(false)

    const {
        pharmacyGetAuthenticationPhoneNumberResponse,
        pharmacyUpdateAuthenticationPhoneNumberResponse
    } = useCommonReducerSelector()

    const dispatch = useDispatch()

    const normalizePhoneNumber = (value = '') => {
        const cleanedValue = String(value ?? '').replace(/[^\d+]/g, '')

        if (!cleanedValue) {
            return ''
        }

        if (cleanedValue.startsWith('+')) {
            return `+${cleanedValue.slice(1).replace(/\+/g, '')}`
        }

        return cleanedValue.replace(/\+/g, '')
    }

    const formatPhoneNumber = (value) => {
        if (!value) return ''

        const normalizedValue = normalizePhoneNumber(value)
        const phoneNumber = normalizedValue.startsWith('+') ? normalizedValue.slice(1) : normalizedValue

        if (normalizedValue.startsWith('+')) {
            return `+${phoneNumber.slice(0, 15)}`
        }

        const limitedNumber = phoneNumber.slice(0, 10)

        if (limitedNumber.length <= 3) {
            return limitedNumber
        } else if (limitedNumber.length <= 6) {
            return `${limitedNumber.slice(0, 3)}-${limitedNumber.slice(3)}`
        }

        return `${limitedNumber.slice(0, 3)}-${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`
    }

    const handleModal = () => {
        updateSubmittedRef.current = false
        dispatch(PharmacyGetAuthenticationPhoneNumber({ fetchType: 'pharmacyGetAuthenticationPhoneNumber' }))
        setShowModal(true)
    }

    useEffect(() => {
        if (pharmacyGetAuthenticationPhoneNumberResponse?.data) {
            setPrimaryPhoneNumber(formatPhoneNumber(pharmacyGetAuthenticationPhoneNumberResponse?.data?.primary_phone || ''))
            const phones =
                (pharmacyGetAuthenticationPhoneNumberResponse?.data?.auth_phones?.map(i =>
                    formatPhoneNumber(i?.phone_number || '')
                ) || [])

            setAdditionalPhones(phones)
            setAdditionalPhoneErrors(new Array(phones.length).fill(''))
        }
    }, [pharmacyGetAuthenticationPhoneNumberResponse])

    useEffect(() => {
        if (pharmacyUpdateAuthenticationPhoneNumberResponse?.success && !pharmacyUpdateAuthenticationPhoneNumberResponse?.loading) {
            // Refetch the data after successful update to show updated numbers in UI
            dispatch(PharmacyGetAuthenticationPhoneNumber({ fetchType: 'pharmacyGetAuthenticationPhoneNumber' }))
        }
    }, [pharmacyUpdateAuthenticationPhoneNumberResponse?.success, dispatch])

    useEffect(() => {
        // Close modal after data is successfully refetched and UI is updated
        if (updateSubmittedRef.current && 
            pharmacyUpdateAuthenticationPhoneNumberResponse?.success && 
            !pharmacyUpdateAuthenticationPhoneNumberResponse?.loading &&
            !pharmacyGetAuthenticationPhoneNumberResponse?.loading && 
            pharmacyGetAuthenticationPhoneNumberResponse?.data) {
            
            const timer = setTimeout(() => {
                setShowModal(false)
                updateSubmittedRef.current = false
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [pharmacyUpdateAuthenticationPhoneNumberResponse?.success, pharmacyUpdateAuthenticationPhoneNumberResponse?.loading, pharmacyGetAuthenticationPhoneNumberResponse?.data, pharmacyGetAuthenticationPhoneNumberResponse?.loading])

    const handleAddPhone = () => {
        setAdditionalPhones([...additionalPhones, ''])
        setAdditionalPhoneErrors([...additionalPhoneErrors, '']) 
    }

    const handleRemovePhone = (index) => {
        setAdditionalPhones(additionalPhones.filter((_, i) => i !== index))
        setAdditionalPhoneErrors(additionalPhoneErrors.filter((_, i) => i !== index)) 
    }

    const handleChangePhone = (index, value) => {
        const formattedValue = formatPhoneNumber(value)
        setAdditionalPhones(additionalPhones.map((p, i) => (i === index ? formattedValue : p)))

        
        setAdditionalPhoneErrors(additionalPhoneErrors.map((err, i) => (i === index ? '' : err)))
    }

    const validateAdditionalPhones = () => { 
        const errors = additionalPhones.map((phone) => {
            const trimmed = (phone || '').trim()
            if (!trimmed) return '' 
            const normalizedPhone = normalizePhoneNumber(trimmed)
            const isValidPhone = /^\+?\d{10,15}$/.test(normalizedPhone)
            if (!isValidPhone) return 'Phone number must contain 10 to 15 digits and may start with +.'
            return ''
        })
        setAdditionalPhoneErrors(errors)
        return errors.every(e => !e)
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        
        const isValid = validateAdditionalPhones()
        if (!isValid) return

        // Filter out empty phone numbers
        const validPhones = additionalPhones
            .filter(phone => phone && phone.trim() !== '')
            .map(phone => normalizePhoneNumber(phone))

        if (validPhones.length === 0) {
            return
        }

        updateSubmittedRef.current = true
        dispatch(
            PharmacyUpdateAuthenticationPhoneNumber({
                auth_phones: validPhones,
                fetchType: 'pharmacyUpdateAuthenticationPhoneNumber',
            })
        )
        setShowModal(false)
    }


    return (
        <>
            <li onClick={handleModal} className="btnnn">
                <button
                    type="button"
                    className="w-full flex gap-4 items-center px-5 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white grey-color rubik-400 fs-16"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-5 h-5"
                        aria-hidden="true"
                    >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.09 5.18 2 2 0 0 1 5.11 3h3a2 2 0 0 1 2 1.72c.12.9.32 1.77.59 2.6a2 2 0 0 1-.45 2.11L9.1 10.6a16 16 0 0 0 4.3 4.3l1.17-1.15a2 2 0 0 1 2.11-.45c.83.27 1.7.47 2.6.59A2 2 0 0 1 22 16.92z" />
                    </svg>
                    Authentication Phone
                </button>
            </li>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
                    <div className="bg-white rounded-lg w-[500px] ">
                        <div className="flex justify-between items-center px-6 py-4">
                            <h2 className="text-xl font-semibold">Update Authentication Phone Number</h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="bg-tableHeader text-white rounded-full p-1 w-6 h-6 flex items-center justify-center"
                            >
                                <ModalCloseIcon />
                            </button>
                        </div>

                        <hr className="border-t border-gray-200" />

                        {(pharmacyGetAuthenticationPhoneNumberResponse?.loading || (updateSubmittedRef.current && pharmacyUpdateAuthenticationPhoneNumberResponse?.success && !pharmacyGetAuthenticationPhoneNumberResponse?.data)) ? (
                            <div className="flex justify-center items-center h-[200px]">
                                <ProfilePicLoader color="black" />
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-1">Primary Phone Number</label>
                                    <input
                                        type="tel"
                                        value={primaryPhoneNumber}
                                        disabled
                                        className="w-full border border-gray-200 bg-gray-100 rounded-md p-2 text-gray-500 cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center mb-1">
                                        <label className="block text-sm font-semibold">Additional Phone Numbers</label>
                                        <button
                                            type="button"
                                            onClick={handleAddPhone}
                                            className="ml-2 text-green-700 text-lg font-bold focus:outline-none"
                                        >
                                            +
                                        </button>
                                    </div>

                                    {additionalPhones?.map((value, idx) => (
                                        <div key={idx} className="mb-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="tel"
                                                    value={value}
                                                    onChange={e => handleChangePhone(idx, e.target.value)}
                                                    className={`w-full border rounded-md p-2 ${additionalPhoneErrors[idx] ? 'border-red-500' : 'border-gray-200'}`}
                                                    placeholder="Enter Phone Number"
                                                    inputMode="tel"
                                                    maxLength={16}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePhone(idx)}
                                                    className="ml-2 text-red-600 hover:text-red-800 text-xl"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                                                    </svg>
                                                </button>
                                            </div>

                                            
                                            {additionalPhoneErrors[idx] && (
                                                <p className="mt-1 text-sm text-red-600">
                                                    {additionalPhoneErrors[idx]}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <button type="submit" className="w-full bg-blue-700 text-white rounded py-2 flex justify-center items-center">
                                    {pharmacyUpdateAuthenticationPhoneNumberResponse?.loading && <ProfilePicLoader color="white" />}
                                    <span className="ml-2">Update</span>
                                </button>

                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default UpdateAuthenticationNumber
